<template>
  <div
    class="fts-client fts-studio fts-color--default"
    :data-studio-load="loading ? 'loading' : 'loaded'"
    ref="studio"
    v-if="isReady"
  >
    <!-- sidenav -->
    <div
      class="client-sidenav"
      @mouseenter="sidebarMouseEvent"
      @mouseleave="sidebarMouseEvent"
      ref="sidenav"
    >
      <div class="client-sidenav-header">
        <div class="client-sidenav-wrapper">
          <img :src="sidenavLogo" class="client-sidenav-logo" />
        </div>
      </div>
      <div class="client-sidenav-body fts-scrollbar fts-scrollbar-overlay">
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          :content="workspace.name"
          :options="{ placement: 'left' }"
          class="sidenav-menu"
          v-for="workspace in preWorkspaces"
          :key="workspace.id"
        >
          <div
            slot="trigger"
            class="sidenav-menu-wrapper"
            :class="{ active: workspaceId == workspace.id }"
            @click="selectWorkspace(workspace)"
          >
            <div class="sidenav-workspace">
              {{workspace.shortName}}
            </div>
          </div>
        </fts-tippy>
        <!-- dashboard -->
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          content="Dashboard"
          :options="{ placement: 'left' }"
          class="sidenav-menu"
        >
          <div
            slot="trigger"
            class="sidenav-menu-wrapper"
            @click="redirectDashboard"
          >
            <font-awesome-icon
              :icon="['fas', 'chart-column']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </div>
        </fts-tippy>
        <!-- users -->
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          content="Users"
          :options="{ placement: 'left' }"
          class="sidenav-menu"
        >
          <router-link
            slot="trigger"
            :to="{ name: 'users' }"
            class="sidenav-menu-wrapper"
          >
            <font-awesome-icon
              :icon="['fas', 'user-friends']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </router-link>
        </fts-tippy>
        <!-- invitation -->
        <fts-tippy
          triggerClass="sidenav-menu-outer"
          content="Invite"
          :options="{ placement: 'left' }"
          class="sidenav-menu"
        >
          <div
            slot="trigger"
            class="sidenav-menu-wrapper"
            @click="openInvitation"
          >
            <font-awesome-icon
              :icon="['fas', 'user-plus']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </div>
        </fts-tippy>
      </div>
      <!-- sidenav footer -->
      <div class="client-sidenav-footer">
        <!-- help -->
        <fts-tippy
          trigger-class="sidenav-menu-outer"
          content="Help"
          :options="{ placement: 'left' }"
          class="sidenav-menu"
        >
          <a
            slot="trigger"
            :href="discordURL"
            target="_blank"
            class="sidenav-menu-wrapper"
          >
            <font-awesome-icon
              :icon="['far', 'question-circle']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </a>
        </fts-tippy>
        <!-- discord -->
        <fts-tippy
          trigger-class="sidenav-menu-outer"
          content="Have any questions? Click and join our Discord support"
          :options="{ placement: 'left' }"
          class="sidenav-menu"
        >
          <a
            slot="trigger"
            :href="discordURL"
            target="_blank"
            class="sidenav-menu-wrapper"
          >
            <font-awesome-icon
              :icon="['fab', 'discord']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </a>
        </fts-tippy>
        <!-- client -->
        <fts-tippy
          trigger-class="sidenav-menu-outer"
          content="Run app"
          :options="{ placement: 'left' }"
          class="sidenav-menu apex"
        >
          <div
            slot="trigger"
            class="sidenav-menu-wrapper"
            @click="runApp()"
            @mousedown.middle="runApp(true)"
          >
            <font-awesome-icon
              :icon="['fas', 'play']"
              fixed-width
              size="lg"
              class="sidenav-icon"
            />
          </div>
        </fts-tippy>
        <!-- profile -->
        <fts-profile
          :profile="profile"
          :profile-photo-size="30"
          :profile-name="false"
          profile-photo-class="user-profile-avatar"
          class="client-user-profile"
          @click.native="openUserProfileModal"
        />
      </div>
    </div>
    <!-- drawer -->
    <div
      class="client-drawer"
      :class="[
        {'collapsed': !isDrawerStatic },
        {'close': !isDrawerStatic && !isDrawerVisible }
      ]"
      @mouseenter="sidebarMouseEvent"
      @mouseleave="sidebarMouseEvent"
      ref="drawer"
    >
      <div class="client-drawer-header">
        <fts-dropdown-select
          class="client-dropdown"
          append-toggle-class="client-dropdown-toggle"
          content-class="client-content client-dropdown-menu"
          :offset="[0, 6]"
          @show="showWorkspaceOptions"
          @selected="selectWorkspaceOption"
        >
          <!-- toggle -->
          <template #toggle>
            <div class="workspace-toggle">
              <div class="workspace-toggle__label">
                {{workspace.name || "Workspace"}}
              </div>
              <font-awesome-icon
                :icon="['fas', 'caret-down']"
                class="workspace-toggle__caret"
              />
            </div>

            <fts-tippy
              :content="isDrawerStatic ? 'Close sidebar' : 'Lock sidebar open'"
              trigger-class="client-drawer-toggle"
              stop-propagation
              class="client-drawer-toggle-wrapper"
              @trigger:click="toggleDrawerMode"
              v-if="drawerToggleButton"
            >
              <template #trigger>
                <font-awesome-icon :icon="['fas', 'angle-double-left']" v-if="isDrawerStatic"/>
                <font-awesome-icon :icon="['fas', 'thumbtack']" v-else />
              </template>
            </fts-tippy>
          </template>
          <!-- menu -->
          <template #menu="{ select, defaultOptionClass }">
            <div class="client-dropdown-item">
              <div class="client-dropdown-search">
                <div class="fts-form-control-filter">
                  <input
                    type="text"
                    class="fts-form-control"
                    v-model.trim="workspaceSearch"
                    placeholder="Search workspace"
                    ref="workspaceSearch"
                  />
                  <div class="search-icon">
                    <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                  </div>
                </div>
              </div>
              <template v-if="workspaceOptionList && workspaceOptionList.length">
                <div
                  v-for="workspace in workspaceOptionList"
                  :key="workspace.id"
                  :class="defaultOptionClass"
                  @click="select(workspace)"
                >
                  <div>{{workspace.name}}</div>
                  <fts-tippy
                    :content="workspace.description"
                    class="client-dropdown-option-description"
                    v-if="workspace.description"
                  >
                    {{workspace.description}}
                  </fts-tippy>
                </div>
              </template>
              <div class="client-dropdown-no-found" v-else>
                <div class="client-dropdown-no-found__icon">
                  <font-awesome-icon :icon="['fas', 'search']" />
                </div>
                <div class="client-dropdown-no-found__label">
                  No matching results
                </div>
              </div>
            </div>

            <div class="client-dropdown-item">
              <div
                class="dropdown-option dropdown-option--secondary client-dropdown-label"
                @click="select('edit')"
                v-if="manageWorkspaceInMenu"
              >
                <div class="dropdown-option-icon">
                  <font-awesome-icon :icon="['fas', 'cog']" fixed-width />
                </div>
                <div>Manage workspace</div>
              </div>

              <div :class="defaultOptionClass" class="client-dropdown-label" @click="select('create')" >
                <font-awesome-icon
                  :icon="['fas', 'plus']"
                  fixed-width
                  class="client-dropdown-label__icon"
                />
                Create a new workspace
              </div> 
            </div>
          </template>
        </fts-dropdown-select>
      </div>
      <div class="client-drawer-body fts-scrollbar fts-scrollbar-overlay">
        <!-- module -->
        <fts-module-designer
          v-model="workspace.menus"
          @module:select="selectModule"
          @module:delete="deleteModule"
          @list:select="selectList"
          @change="autoSave"
        />
        <div class="drawer-action-wrapper">
          <div
            class="drawer-action"
            @click="openModuleCreateModal"
          >
            <font-awesome-icon :icon="['fas', 'plus']" class="drawer-action-icon" />
            <div>Create New Module</div>
          </div>
        </div>
      </div>
      <div class="client-drawer-footer">
        <fts-profile
          :profile="profile"
          :profile-photo="false"
          profile-name-class="user-profile-info"
          class="client-user-profile"
          @click.native="openUserProfileModal"
        >
          <template #profileName="{ userProfileName, userProfileUsername }">
            <div class="user-profile-wrapper">
              <div class="user-profile-name">{{ userProfileName }}</div>
              <div class="user-profile-role">{{ userRoles }}</div>
            </div>
            <div
              class="user-profile-username client-drawer-username"
              v-if="userProfileUsername"
            >{{ userProfileUsername }}</div>
          </template>
        </fts-profile>
      </div>
    </div>
    <!-- content -->
    <div class="studio-content">
      <div class="studio-content-broadcast" v-if="browserIncompatible">
        Improve your experience, we recommend using the latest version of Chrome or Microsoft Edge (Chromium). 
      </div>
      <!-- content container -->
      <div
        class="studio-content-container"
        :class="getPageClass"
        @scroll="onScroll"
        ref="studioContentContainer"
      >
        <div class="content-header">
          <div class="content-nav">
            <!-- nav-l -->
            <div class="content-nav-l">
              <div class="module-wrapper" ref="module-wrapper" v-if="vectorId">
                <fts-moji :size="20" :emoji="vector.emoji" class="module-emoji" v-if="vector.emoji" />
                <div class="module-name">{{ vector.name }}</div>
                <div class="module-tag" v-if="vector.isUserCollection">
                  <font-awesome-icon :icon="['fas', 'user']" fixed-width />
                </div>
              </div>
            </div>
            <!-- nav-c -->
            <div class="content-nav-c">
              <div class="content-nav-wrapper">
                <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" fixed-width class="content-nav-icon" />
                <div class="content-nav-title">Studio</div>
                <!-- workflow -->
                <fts-tippy
                  content="Open flow diagram"
                  class="content-nav-action"
                  v-if="isCapableOf(SYSTEM_CAPABILITY.Workflow) && vectorId"
                >
                  <fts-btn slot="trigger" icon text @click="workflow">
                    <font-awesome-icon :icon="['fas', 'project-diagram']" fixed-width />
                  </fts-btn>
                </fts-tippy>
              </div>
            </div>
            <!-- nav-r -->
            <div class="content-nav-r">
              <div class="content-nav-actions">
                <template v-if="vectorId && !isDashboard && !Boolean(moduleCreateMode)">
                  <!-- auto push -->
                  <fts-checkbox
                    :size="16"
                    v-model="autoPush"
                    class="content-nav-checkbox"
                    v-if="isBeta"
                  >
                    <div style="white-space: nowrap;">Auto Push <fts-release-badge class="fts-align-top" /></div>
                  </fts-checkbox>
                  <!-- force submit -->
                  <fts-checkbox
                    :size="16"
                    v-model="forceSubmit"
                    class="content-nav-checkbox"
                    v-if="isBeta"
                  >
                    <div style="white-space: nowrap;">Skip Validation <fts-release-badge class="fts-align-top" /></div>
                  </fts-checkbox>
                  <!-- run client -->
                  <fts-btn
                    sm
                    text
                    color="secondary"
                    :disabled="saving"
                    @click.native.prevent="runApp()"
                    @mousedown.native.middle.prevent="runApp(true)"
                  >
                    <font-awesome-icon :icon="['fas', 'play']" />Run
                  </fts-btn>
                  <!-- save -->
                  <div class="fts-flex fts-items-center">
                    <fts-btn
                      sm
                      text
                      color="primary"
                      :loading="saving"
                      @click="save"
                    >
                      <font-awesome-icon :icon="['fas', 'check']" />Save
                    </fts-btn>

                    <fts-dropdown-select
                      append-to-body
                      overlay
                      content-class="dropdown-menu dropdown-menu-w-xs"
                      :disabled="saving"
                      v-if="isInternal"
                    >
                      <fts-btn
                        slot="toggle"
                        sm
                        icon
                        text
                        color="primary"
                        :disabled="saving"
                      >
                        <font-awesome-icon :icon="['fas', 'caret-down']" />
                      </fts-btn>
                      <template #menu="{ select }">
                        <div class="dropdown-option" @click="saveRevisionConfirmation();select();">
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['fas', 'check']" />
                          </div>
                          <div>Save as New Revision</div>
                        </div>
                      </template>
                    </fts-dropdown-select>
                  </div>
                </template>
                <!-- create:cancel -->
                <fts-btn
                  sm
                  text
                  @click="cancelModuleCreate"
                  v-if="moduleCreateMode === 'template'"
                >
                  Cancel
                </fts-btn>
              </div>
            </div>
          </div>
        </div>
        <!-- dashboard -->
        <fts-dashboard v-if="isDashboard" />
        <!-- console -->
        <fts-console
          ref="console"
          :canvas-props="canvas"
          :vector-id="vectorId"
          :vector-props="vector"
          :validator="$v"
          @scrollTo="studioContentScrollTo"
          @update="save()"
          @module:selected="selectModule($event)"
          @cancel="cancelModuleCreate"
          @deleteModule="deleteModule"
          v-if="vectorId || moduleCreateMode === 'create'"
        />
        <!-- module packages -->
        <fts-module-packages
          @package:installed="openModuleOnboardModal"
          v-else-if="moduleCreateMode === 'template'"
        />

        <fts-spinner show full-screen boundary="parent" v-if="loading" />
      </div>
    </div>  
    <!-- user profile -->
    <fts-user-profile ref="ftsUserProfile" />
    <!-- invitation -->
    <fts-invitation-modal ref="ftsInvitationModal" />
    <!-- workflow -->
    <fts-workflow ref="ftsWorkflow" />
    <!-- console validator -->
    <fts-console-validator />
    <!-- confirmation -->
    <v-dialog />
    <!-- module create modal -->
    <fts-module-create-modal ref="ftsModuleCreateModal" />
    <!-- module import design modal -->
    <fts-module-import-design-modal ref="ftsModuleImportDesignModal" />
    <!-- module onboard -->
    <fts-module-onboard-modal ref="ftsModuleOnboard" />
    <!-- workspace modal -->
    <fts-workspace-modal ref="ftsWorkspaceModal" />
    <!-- create workspace modal -->
    <fts-workspace-create-modal ref="workspaceCreateModal" @created="onWorkspaceCreated" />
    <!-- confirmation -->
    <fts-confirmation global />
    <!-- GNey configuration copilot (studio-gney proposal; alpha-gated D14/D6) -->
    <fts-client-teammate surface="studio" v-if="gneyStudioEnabled" />
  </div>
</template>

<script>
import Vue from 'vue';
import VueCookies from 'vue-cookies';
Vue.use(VueCookies);
import VueRouter from 'vue-router';
Vue.use(VueRouter);
import Vuex from 'vuex';
Vue.use(Vuex);
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);
import { required } from "vuelidate/lib/validators";
import changesets from "diff-json";
import signalr from './components/store/signalr';

import webAppConfig from './components/commons/webApp.config';
import commons from './components/commons/commons';
import { SYSTEM_CAPABILITY, isCapableOf } from './components/commons/flags';
import moduleGuard from './components/commons/moduleGuard';
import canvasDesign from './components/commons/canvasDesign';
import designValidator from './components/commons/designValidator';
import { store } from './components/store/canvasDesignStore';
import { authorizationStore } from './components/store/authorizationStore';
import { applyDisplay } from './components/commons/readability';
import { classificationStore } from './components/store/classificationStore';
import LocalStorageService from './components/store/LocalStorageService';

import FTSCheckbox from './components/elements/FTSCheckbox';
import FTSDashboard from './components/design/dashboard/FTSDashboard';
import FTSConsole from './components/design/FTSConsole';
import FTSDropdownSelect from './components/elements/FTSDropdownSelect';
import FTSModuleDesigner from './components/design/FTSModuleDesigner';
import FTSMoji from './components/elements/FTSMoji';
import FTSBtn from './components/elements/FTSBtn';
import FTSModal from './components/elements/FTSModal';
import FTSEmoji from './components/elements/FTSEmoji';
import FTSConsoleValidator from './components/design/FTSConsoleValidator';
import FTSUserProfile from './components/elements/FTSUserProfile';
import FTSProfile from './components/elements/FTSProfile';
import FTSTippy from './components/elements/FTSTippy';
import FTSInvitationModal from './components/client/FTSInvitationModal';
import FTSWorkflow from './components/elements/FTSWorkflow';
import FTSModuleCreateModal from './components/design/FTSModuleCreateModal';
import FTSModuleImportDesignModal from './components/design/FTSModuleImportDesignModal';
import FTSModuleOnboardModal from './components/design/FTSModuleOnboardModal';
import FTSModulePackages from './components/design/FTSModulePackages';     
import FTSWorkspaceModal from './components/design/FTSWorkspaceModal';
import { settingsStageAllowed } from './components/settings/settingsStage';
import FTSWorkspaceCreateModal from './components/client/FTSWorkspaceCreateModal';
import FTSConfirmation from './components/elements/confirmation/FTSConfirmation';
import FTSSpinner from './components/elements/FTSSpinner';

export default {
  name: "app",
  metaInfo() {
    return { title: this.metaTitle };
  },
  components: {
    "fts-checkbox": FTSCheckbox,
    "fts-dashboard": FTSDashboard,
    "fts-console": FTSConsole,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-module-designer": FTSModuleDesigner,
    "fts-moji": FTSMoji,
    "fts-btn": FTSBtn,
    "fts-modal": FTSModal,
    "fts-workspace-create-modal": FTSWorkspaceCreateModal,
    "fts-emoji": FTSEmoji,
    "fts-console-validator": FTSConsoleValidator,
    "fts-user-profile": FTSUserProfile,
    "fts-profile": FTSProfile,
    "fts-tippy": FTSTippy,
    "fts-invitation-modal": FTSInvitationModal,
    "fts-workflow": FTSWorkflow,
    "fts-module-create-modal": FTSModuleCreateModal,
    "fts-module-import-design-modal": FTSModuleImportDesignModal,
    "fts-module-onboard-modal": FTSModuleOnboardModal,
    "fts-module-packages": FTSModulePackages,
    "fts-workspace-modal": FTSWorkspaceModal,
    "fts-confirmation": FTSConfirmation,
    "fts-spinner": FTSSpinner,
    // Async on purpose: code-splits the chat panel out of the Studio's initial bundle,
    // and keeps the heavy client-store graph out of module scope until the gate mounts it.
    "fts-client-teammate": () => import('./components/elements/FTSClientTeammate')
  },
  store,
  data() {
    return {
      SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
      isReady: false,
      loaded: false,
      loading: false,
      isDrawerStatic: true,
      isDrawerVisible: true,
      drawerToggleButton: true, 
      saving: false,
      workspaceLoading: false,
      workspaceSearch: null,
      workspaceOptionList: [],
      workspaceOptionRequest: 0,
      preWorkspaces: [],
      moduleCreateMode: null,
      userProfileCKey: 0,
    };
  },
  validations() {
    let canvas = {};
    let vector = {};
    /* information */
    if (this.canvas.information && this.canvas.information.length) {
      canvas.information = {
        $each: {
          ...designValidator.informationDesign(this.canvas.information),
          $trackBy: "id"
        }
      };
    }
    /* activities */
    if (this.canvas.activities && this.canvas.activities.length) {
      canvas.activities = {
        $each: {
          ...designValidator.activityDesign(this.canvas.activities),
          $trackBy: "id"
        }
      };
    }
    /* states */
    if (this.canvas.states && this.canvas.states.length) {
      canvas.states = {
        $each: {
          ...designValidator.stateDesign(this.canvas.states),
          $trackBy: "id"
        }
      };
    }
    /* flows */
    if (this.canvas.flows && this.canvas.flows.length) {
      canvas.flows = {
        $each: {
          ...designValidator.flowDesign(this.canvas.flows),
          $trackBy: "id"
        }
      };
    }
    /* module */
    vector = designValidator.moduleDesign(this.workspaceModules);
    return { canvas, vector };
  },
  computed: {
    // "Manage workspace" left the workspace menu once settings went live: the avatar is the one way
    // in (KY, 2026-10-01). It comes back only with the kill switch, as the old dialog's door.
    manageWorkspaceInMenu() {
      return !settingsStageAllowed();
    },
    metaTitle() {
      let title = ['Inistate Studio'];
      this.workspace.name && title.push(this.workspace.name);
      this.vector.name && title.push(this.vector.name);
      return title.reverse().join(' - ');
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    // GNey studio copilot gate (D14/D6): internal identities, or workspaces with the gney
    // flag (rides the workspace payload). Mounted only once a module context can exist;
    // the server's own D15 workspace-scoped design gate is the real boundary (403).
    gneyStudioEnabled() {
      return this.loaded && this.$store.getters.canvasOpsEnabled;
    },
    isBeta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
    browserIncompatible() {
      return navigator.userAgent.indexOf('Chrome') == -1;
    },
    sidenavLogo() {
      return authorizationStore.getters.appLogo;
    },
    workspace() {
      return this.$store.state.workspace || {};
    },
    workspaceId() {
      return this.$store.getters.workspaceId;
    },
    workspaceModules() {
      return this.$store.getters.workspaceModules;
    },
    profile() {
      return authorizationStore.getters.userProfile;
    },
    userRoles() {
      return authorizationStore.getters.userRoles;
    },
    vector() {
      const module = this.$store.state.vector || {};
      if (module.id && Boolean(this.moduleCreateMode)) {
        this.moduleCreateMode = null;
      }
      return module;
    },
    vectorId() {
      return this.vector.id;
    },
    originalVector() {
      return this.$store.state.originalVector;
    },
    autoPush: {
      get() {
        return this.$store.getters.autoPush;
      },
      set(val) {
        this.$store.dispatch("setAutoPush", val);
        const preference = {
          ...LocalStorageService.getStudioPreference(webAppConfig.baseServer, this.workspaceId),
          autoPush: val
        }
        LocalStorageService.setStudioPreference(webAppConfig.baseServer, this.workspaceId, preference);
      }
    },
    forceSubmit: {
      get() {
        return this.$store.getters.forceSubmit;
      },
      set(val) {
        this.$store.dispatch("setForceSubmit", val);
        const preference = {
          ...LocalStorageService.getStudioPreference(webAppConfig.baseServer, this.workspaceId),
          forceSubmit: val
        }
        LocalStorageService.setStudioPreference(webAppConfig.baseServer, this.workspaceId, preference);
      }
    },
    canvas() {
      return this.$store.state.canvas || {};
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    filteredWorkspaces() {
      let searchKey = this.workspaceSearch ? this.workspaceSearch : "";
      return this.workspaces.filter(
        x => x.name.toLowerCase().indexOf(searchKey.toLowerCase()) != -1
      );
    },
    clientRoute() {
      if (this.vectorId) {
        let listId = null;
        try {
          // canvasMenus holds real views only (the legacy placeholder row is filtered out).
          const menu = this.$store.getters.canvasMenus[0];
          if (menu && menu.id) {
            listId = menu.id;
          }
        } catch(err) {
          console.warn('Studio:ClientRoute', err);
        }

        return commons.clientListViewRoute({
          moduleId: this.vectorId,
          listId: listId
        });
      } else {
        return { name: 'dashboard' }
      }
    },
    isDashboard() {
      return (
        this.$route.query &&
        this.$route.query.wsId &&
        !Boolean(this.moduleCreateMode) &&
        !this.vectorId
      );
    },
    getPageClass() {
      if (this.vectorId || this.moduleCreateMode === 'create') {
        return "studio-content-create";
      } else if (this.moduleCreateMode === 'template') {
        return "studio-content-template";
      }
    },
  },
  watch: {
    workspaceSearch: _.debounce(async function() {
      await this.loadWorkspaceOptions({
        search: this.workspaceSearch
      });
    }, 500)
  },
  methods: {
    isCapableOf(type) {
      return isCapableOf(type, this.workspaceModules, this.vectorId);
    },
    toggleDrawerMode() {
      this.isDrawerStatic = !this.isDrawerStatic;
    },
    sidebarMouseEvent: _.debounce(function(evt) {
      if (this.isDrawerStatic === false) {
        if (evt.type === 'mouseenter') {
          this.isDrawerVisible = true;
        } else if (evt.type === 'mouseleave') {
          if (
            evt && evt.toElement &&
            (
              this.$refs.sidenav && this.$refs.sidenav.contains(evt.toElement)
              || this.$refs.drawer && this.$refs.drawer.contains(evt.toElement)
              || commons.elementContainClassName(evt.toElement, 'sidebar-relative-el')
            )
          ) { return; }
          this.isDrawerVisible = false;
        }
      }
    }, 300),
    onScroll(event) {
      window.eventBus.$emit("ftsConsoleOnScroll");

      if (this.$refs['module-wrapper']) {
        if (event.target.scrollTop >= 38) {
          this.$refs['module-wrapper'].classList.add('module-wrapper--visible');
        } else {
          this.$refs['module-wrapper'].classList.remove('module-wrapper--visible');
        }
      }
    },
    async runApp(targetBlank) {
      const dialogConfirmation = () =>
        new Promise(resolve => {
          const toSave = moduleGuard.compactModule(this.vector);
          const canvasDiffs = changesets.diff(this.originalVector, toSave);
          if (canvasDiffs && canvasDiffs.length) {
            this.$modal.show("dialog", {
              title: "Save changes?",
              text: "Unsaved changes will be lost. Save changes before continue?",
              buttons: [
                {
                  title: "No. Stay",
                  handler: () => {
                    this.$modal.hide("dialog");
                    resolve(false);
                  }
                },
                {
                  title: targetBlank ? "Continue without save" : "Discard and continue",
                  handler: () => {
                    this.$modal.hide("dialog");
                    resolve(true);
                  }
                },
                {
                  title: "Save and continue",
                  handler: async () => {
                    this.$modal.hide("dialog");
                    await this.save();
                    resolve(true);
                  }
                },
              ]
            });
          } else {
            resolve(true);
          }
        });

      const continueClient = (targetBlank) => {
        if (targetBlank) {
          const routeData = this.$router.resolve(this.clientRoute);
          window.open(routeData.href, '_blank');
        } else {
          this.$router.push(this.clientRoute);
        }
      }
      if (this.vectorId) {
        const res = await dialogConfirmation();
        res && continueClient(targetBlank);
      } else {
        continueClient(targetBlank);
      }
    },
    openInvitation() {
      this.$refs.ftsInvitationModal.open({ workspaceName: this.workspace.name });
    },
    async openUserProfileModal() {
      // The avatar is the way into settings, here as in the Client and Lite shells; the dialog
      // is only the kill switch's fallback.
      if (settingsStageAllowed()) {
        this.$router.push({ name: "settings", params: { section: "profile" } }).catch(() => {});
        return;
      }
      await this.$refs.ftsUserProfile.show();
    },
    async selectList(menuId) {
      const menu =
        this.canvas &&
        Array.isArray(this.canvas.menus) &&
        this.canvas.menus.find(x => x.id === menuId);
      if (menu)
        this.$refs.console.openConsoleElement(
          { name: "menu", fieldName: "menus" },
          menu
        );
    },
    async loadModule(moduleId) {
      try {
        this.loading = true;
        await this.$store.dispatch("loadVector", moduleId);

        if (
          this.$route.query &&
          (commons.decodeids(this.$route.query.id) != this.vectorId) 
        ) {
          this.$router.push({ query: { id: commons.encodeids(this.vectorId)}})
        }
      } catch(err) {
        if (
          err.response && 
          err.response.data && 
          err.response.data.message
        ) {
          this.$toasted.error(err.response.data.message);
        } else {
          this.$toasted.error("unable load module");
        }

        setTimeout(() => {
          this.$router.push({ query: { wsId: ""}});
          this.$router.go();
        }, 1500);
      } finally {
        this.loading = false;
      }
    },
    async selectModule(module) {
      if (this.vectorId == module.id) {
        return;
      }
      // !! issue, store cant find state => state.currentWorkspace
      // if (!module.currentWorkspace && module.ownerWorkspaceId != this.$store.state.currentWorkspace.id) {
      if (
        !module.currentWorkspace &&
        module.ownerWorkspaceId != this.workspaceId
      ) {
        if (
          !confirm(
            "This module is not own by current workspace, are you sure to change workspace?"
          )
        ) {
          return;
        }
      }

      const toSave = moduleGuard.compactModule(this.vector);
      const canvasDiffs = changesets.diff(this.originalVector, toSave);

      if (this.vectorId && canvasDiffs && canvasDiffs.length) {
        this.$modal.show("dialog", {
          title: "Unsaved changes",
          text: `Are you sure discard changes on ${this.vector.name} ?`,
          buttons: [
            {
              title: "OK. Discard Changes",
              default: true,
              handler: async () => {
                this.$modal.hide("dialog");
                await this.loadModule(module.id);
              }
            },
            {
              title: "No. Stay"
            }
          ]
        });
      } else {
        await this.loadModule(module.id);
      }
    },
    /* module:create */
    cancelModuleCreate() {
      this.moduleCreateMode = null;
      this.openModuleCreateModal();
    },
    async openModuleCreateModal() {
      const res = await this.$refs.ftsModuleCreateModal.show();
      if (res === 'create' || res === 'template') {
        this.moduleCreateMode = res;
        this.$store.dispatch('resetVector');
        this.$router.push({
          query: { wsId: commons.encodeids(this.workspaceId) }
        });
      } else if (res === 'spreadsheet') {
        setTimeout(() => {
          this.openModuleImportDesignModal();
        }, 300);
      }
    },
    async openModuleImportDesignModal() {
      const res = await this.$refs.ftsModuleImportDesignModal.show();
      if (res) {
        await this.$store.dispatch("loadVector", res);
        this.$store.dispatch("updateWorkspaceMenus");
        this.$router.push({
          query: { id: commons.encodeids(this.vectorId)}
        });
        this.moduleCreateMode = null;
        setTimeout(() => {
          this.openModuleOnboardModal();
        }, 300);
      } else if (res === false) {
        setTimeout(() => {
          this.cancelModuleCreate();
        }, 300);
      }
    },
    openModuleOnboardModal() {
      this.$refs.ftsModuleOnboard.show();
    },
    /* module:delete */
    async deleteModule(item) {
      const res = await this.$confirmation.show({
        type: "delete",
        title: "Module",
        text: item.name,
        textEmoji: item.emoji,
        enableConfirmationKey: true
      });

      if (res === true) {
        try {
          await this.$store.dispatch("deleteVector", item.id);
          this.$router.replace({ query: { wsId: this.workspaceId } });
          this.$toasted.success(`${item.name} has been deleted.`);
        } catch (err) {
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            this.$toasted.error(
              "Failed to Delete: " + err.response.data.message
            );
          } else {
            console.error(err);
            this.$toasted.error("Failed to Delete");
          }
        }
      }
    },
    async showWorkspaceOptions() {
      this.workspaceSearch = "";
      await this.loadWorkspaceOptions({
        search: this.workspaceSearch,
        workspaceId: this.workspaceId
      });

      this.$nextTick(() => {
        this.$refs.workspaceSearch.focus();
      });
    },
    /**
     * Fills the workspace switcher's list. Only the newest request may: opening fetches every
     * workspace and typing fetches the matches 500 ms later, so a slow opening response used to
     * land last and put the full list back under the search text (see Client.vue).
     */
    async loadWorkspaceOptions(payload) {
      const request = ++this.workspaceOptionRequest;
      const list = await this.$store.dispatch("searchWorkspace", payload);
      if (request !== this.workspaceOptionRequest) return;
      this.workspaceOptionList = list;
    },
    async loadPreWorkspaces() {
      try {
        this.preWorkspaces = [{
          id: this.workspace.id, 
          name: this.workspace.name,
          shortName: commons.getWorkspaceShortName(this.workspace.name)
        }];

        const res = await this.$store.dispatch("searchWorkspace", {
          search: ""
        });

        this.preWorkspaces = this.preWorkspaces.concat(
          ...res
            .filter(x => x.id != this.workspace.id)
            .slice(0, 2)
            .map(x => ({
              id: x.id, 
              name: x.name, 
              shortName: commons.getWorkspaceShortName(x.name)
            }))
        );
      } catch(err) {
        console.error(err);
      }
    },
    async selectWorkspace(workspace) {
      if (workspace.id != this.workspace.id) {
        try {
          this.workspaceLoading = true;

          if (Boolean(this.moduleCreateMode)) {
            this.moduleCreateMode = null;
          }

          await this.$store.dispatch("selectWorkspace", { id: workspace.id });
          this.$store.dispatch("resetVector");

          this.$router.push({
            query: { wsId: commons.encodeids(this.workspace.id)}
          });

          if (!(this.workspaceModules && this.workspaceModules.length)) {
            this.openModuleCreateModal();
          }
        } catch (err) {
          this.$toasted.global.error({
            response: err.response,
            message: "Oops! Unable to load the workspace at the moment. Please try again."
          });
        } finally {
          this.workspaceLoading = false;
        }
      }
    },
    async selectWorkspaceOption(item) {
      if (item == "edit") {
        // Workspace settings, as from the Client and Lite menus. The Client shell opens this same
        // workspace (the wsId cookie the studio store keeps). The dialog is only the kill
        // switch's fallback now. W-25.
        if (settingsStageAllowed()) {
          this.$router.push({ name: "settings", params: { section: "overview" } }).catch(() => {});
          return;
        }
        const res = await this.$refs.ftsWorkspaceModal.show({ id: this.workspaceId});
        if (res && res.name) {
          this.$store.dispatch("setWorkspaceName", res.name);
          let preWorkspace = this.preWorkspaces.find(x => x.id === res.id);
          preWorkspace && (preWorkspace.name = res.name);
        } else if (res && res.type == "delete") {
          this.workspaceLoading = true;
          let selectedWorkspaceId = null;

          if (Array.isArray(this.preWorkspaces)) {
            const preWorkspace = this.preWorkspaces.find(x => x.id != res.id);
            selectedWorkspaceId = preWorkspace && preWorkspace.id;
          }

          if (!selectedWorkspaceId) {
            try {
              selectedWorkspaceId = await this.$store.dispatch("getFirstOrDefaultWorkspaceId");
            } catch(err) {
              console.error(err);
            }
          }

          if (selectedWorkspaceId) {
            await this.selectWorkspace({ id: selectedWorkspaceId });
            await this.loadPreWorkspaces();
          } else {
            location.reload(true);
          }
        } else if (res && res.id) {
          this.selectWorkspace({ id: res.id });
        }
      } else if (item == "create") {
        this.$refs.workspaceCreateModal.open();
      } else {
        this.selectWorkspace(item);
      }
    },
    async onWorkspaceCreated(workspaceId) {
      await this.$store.dispatch("selectWorkspace", { id: workspaceId });

      if (this.workspaceId != workspaceId) {
        this.$toasted.global.error(new Error("Workspace was created, but we couldn't switch to it. Please refresh and try again."));
        return;
      }

      this.$router.push({ query: { wsId: commons.encodeids(workspaceId) } });

      if (!this.workspaceModules.length) {
        this.$nextTick(() => {
          this.openModuleCreateModal();
        });
      }
    },
    workflow() {
      this.$openStudioWorkflow();
    },
    async save({ moduleRevisionDescription, newVersion, forceSubmit: _forceSubmit } = {}) {
      if (this.saving) return;

      this.$v.canvas.$touch();
      if (this.vectorId) {
        this.$v.vector.$touch();
      }

      if (this.$v.canvas.$invalid) {
        window.eventBus.$emit("consoleValidator", { validations: this.$v.canvas});
      } else if (this.$v.vector.$invalid && this.vectorId) {
        this.studioContentScrollTo();
        this.$toasted.error(`Studio: ${designValidator.moduleDesignError(this.$v.vector, "name")}`);
      } else {
        this.saving = true;
        try {
          this.$store.dispatch("setIsSaving", true);

          /* the Force Submit option bypasses the consistency check and submits directly */
          let forceSubmit = _forceSubmit || this.forceSubmit;
          /* consistency check */
          if (this.vectorId && !forceSubmit) {
            try {
              const consistency = await this.$store.dispatch("checkConsistency");
              if (!consistency.consistent) {
                console.error('[Consistency Check] Raw response:', JSON.parse(JSON.stringify(consistency)));
                const errorMessages = consistency.report;
                const res = await this.$confirmation.show({
                  title: "Inconsistency Detected",
                  text: errorMessages,
                  iconPack: "material-icons-outlined",
                  icon: "warning",
                  iconColor: "var(--inistate-icon-warning)",
                  buttons: [
                    {
                      title: "Cancel",
                      bind: { text: true }
                    },
                    {
                      title: "Force Submit",
                      bind: { color: "warning" },
                      default: true,
                      handler: ({ resolve }) => resolve(true)
                    }
                  ]
                });
                if (res !== true) return;
                forceSubmit = true;
              }
            } catch (err) {
              console.warn('Consistency check failed, proceeding with save', err);
            }
          }

          let workspaceRes = null;
          let moduleRes = null;
          try {
            /* workspace */
            if (this.workspaceId) {
              workspaceRes = await new Promise(async(resolve, reject) => {
                const toSaveMenus = commons.trimMenu(this.workspace.menus);
                const workspaceMenusDiff = changesets.diff(this.$store.getters.originalWorkspaceMenus, toSaveMenus);
                if (workspaceMenusDiff && workspaceMenusDiff.length) {
                  try {
                    const res = await this.$store.dispatch("saveWorkspace");
                    resolve(res);
                  } catch(err) {
                    reject(err);
                  }
                } else {
                  resolve(null);
                }
              });
            }
            /* module */
            if (this.vectorId) {
              if (
                this.canvas.connectorText != '' &&
                this.canvas.connectorText != null
              ) {
                this.canvas.connector = JSON.parse( this.canvas.connectorText);
                delete this.canvas.connectorText;
              }
              if (
                this.canvas.mappingText != '' &&
                this.canvas.mappingText != null
              ) {
                this.canvas.mapping = JSON.parse( this.canvas.mappingText);
                delete this.canvas.mappingText;
              }

              try {
                canvasDesign.expandDocumentsFormDesign(this.canvas);
              } catch(err) { console.error(err); }

              // Views plus the page design (canvas.listDesign, R13): both carry column designs.
              const columnDesigns = (this.canvas.menus || []).concat(this.canvas.listDesign ? [this.canvas.listDesign] : []);
              canvasDesign.expandMenusDesign(columnDesigns, this.canvas.information);
              canvasDesign.compactMenusDesign(columnDesigns, this.canvas.information);

              moduleRes = await this.$store.dispatch("saveVector", {
                description: moduleRevisionDescription,
                newVersion: newVersion,
                forceSubmit: forceSubmit
              });
            }
            /* select workspace */
            if (workspaceRes && workspaceRes.status === 200) {
              await this.$store.dispatch("selectWorkspace", {
                id: commons.decodeids(this.workspaceId)
              });
            }
            if (Boolean(moduleRes) && moduleRes && moduleRes.data && moduleRes.data.updatedDate) {
              this.$store.dispatch("setWorkspaceLastUpdatedOn", moduleRes.data.updatedDate);
            }
            /* toasted */
            const message = (() => {
              if (Boolean(workspaceRes) && Boolean(moduleRes))
                return 'Studio';
              else if (Boolean(workspaceRes))
                return `Workspace: ${this.workspace.name}`;
              else if (Boolean(moduleRes))
                return `Module: ${this.vector.name}`;
            })();
            if (message)
              this.$toasted.success(`${message} changes have been saved.`);
            else
              this.$toasted.info('No changes found.');
          } catch (err) {
            console.error(err);
            this.$toasted.global.error({
              response: err.response,
              message: 'Studio changes failed to save'
            });
          }
        } finally {
          this.saving = false;
          this.$store.dispatch("setIsSaving", false);
        }
      }
    },
    autoSave: _.debounce(async function() {
      await this.save();
    }, 800),
    async saveRevisionConfirmation() {
      const { res, promptValue } = await this.$confirmation.show({
        type: 'prompt',
        text: 'Are you sure want to save as new revision?'
      });

      if (res == true) {
        this.save({ moduleRevisionDescription: promptValue, newVersion: true });
      }
    },
    redirectDashboard() {
      if (!this.isDashboard) {
        this.moduleCreateMode = null;
        this.$store.dispatch("resetVector");
      }

      this.$router.push({
        query: { wsId: commons.encodeids(this.workspaceId)}
      });
    },
    studioContentScrollTo(coordinate) {
      const x = (coordinate && coordinate.x) || 0;
      const y = (coordinate && coordinate.y) || 0;
      this.$refs.studioContentContainer.scrollTo(x, y);
    }
  },
  created() {
    this.discordURL = commons.discordURL;
    window.eventBus.$on("studio.save", this.save);
  },
  async mounted() {
    /* reading preference: this person's own text size and contrast on this device (FTR00375) */
    applyDisplay(authorizationStore.getters.userName);
    /* reset classification store */
    classificationStore.dispatch("reset");
    /* web app config */
    await webAppConfig.getConfig();
    if (authorizationStore.getters.isDevelopment) {
      /* set theme */
      const setTheme = (theme) => {
        if (theme === 'dark') {
          document.body.classList.add('dark');
          localStorage.setItem("fetias-client-theme", 'dark');
        } else {
          document.body.classList.remove('dark');
          localStorage.setItem("fetias-client-theme", 'light');
        }
      }
      /* window match media */
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', event => {  
          if (
            Boolean(localStorage["fetias-client-theme-sync-os"]) &&
            event.matches
          ) {
            setTheme('dark');
          }
        });

      if (
        (
          Boolean(localStorage["fetias-client-theme-sync-os"]) &&
          window.matchMedia('(prefers-color-scheme: dark)').matches
        ) || 
        (
          localStorage['fetias-client-theme'] &&
          localStorage['fetias-client-theme'] === 'dark'
        )
      ) {
        setTheme('dark');
      }
      /* ready */
      this.isReady = true;
      /* color */
      const color = localStorage['fetias-client-color'];
      if (color) {
        try {
          this.$nextTick(() => {
            this.$refs.studio.classList.add(color);
          });
        } catch(err) {
          console.warn('ClientColor', err);
        }
      }
    } else {
      /* ready */
      this.isReady = true;
    }
    /* profile */
    await authorizationStore.dispatch("profile");
    /* load studio */
    const query = this.$route.query;
    if (query.id) {
      const wsId = LocalStorageService.getWorkspaceId(webAppConfig.baseServer);

      if (wsId) {
        this.$store.dispatch('setClientHeadersWsId', wsId);
      }

      await this.loadModule(commons.decodeids(query.id));
    } else {
      let vm = this;
      let workspaceRequestRetry = false;
      const loadWorkspace = async (wsId) => {
        try {
          const res = await vm.$store.dispatch("loadWorkspace", { id: wsId });
          if (res.id) {
            vm.$router.push({
              query: { wsId: commons.encodeids(res.id) }
            });
          } else {
            throw { id: null };
          }
        } catch(err) {
          if (workspaceRequestRetry) {
            if (err.response && err.response.data && err.response.data.message) {
              vm.$toasted.error(err.response.data.message);
            } else if (err.id === null) {
              vm.$toasted.error("Workspace Unauthorized Access. Redirected.");
            }
            vm.$router.push({ name: 'dashboard' });
            throw new Error('Workspace unauthorized access. Redirect to dashboard.');
          } else {
            workspaceRequestRetry = true;
          }
        }
      }
      await loadWorkspace(query.wsId && commons.decodeids(query.wsId));
      /* get workspace retry */
      if (workspaceRequestRetry) {
        await loadWorkspace();
      }
    }

    if (this.workspaceId) {
      // get preference
      const preference = LocalStorageService.getStudioPreference(webAppConfig.baseServer, this.workspaceId);
      if (preference && preference.hasOwnProperty("autoPush")) {
        this.$store.dispatch("setAutoPush", preference.autoPush);
      }
    }
    /* loaded */
    this.loaded = true;
    /* workspace settings */
    if (this.workspaceId) {
      /* pre-workspaces */ 
      await this.loadPreWorkspaces();
      /* open create module modal */
      if (!this.workspaceModules.length) {
        this.openModuleCreateModal();
      }
      /* signalr */
      signalr.setupCommunication(this.workspaceId);
    }
    /* flow diagram */
    Vue.prototype.$openStudioWorkflow = () => {
      this.$refs.ftsWorkflow.show();
    };
  },
  beforeDestroy() {
    // off eventBus
    window.eventBus.$off("studio.save", this.save);
    // reset store state
    this.$store.dispatch("resetState");
    // hide crisp
    commons.hideCrispChat();
    // remove theme
    document.body.classList.remove("dark");
    // remove instance property
    delete Vue.prototype.$openStudioWorkflow;
  },
};
</script>

<style lang="scss">
@use "../src/assets/scss/vars" as *;
@use "../src/assets/scss/colors" as *;

.fts-studio {
  .studio-content {
    height: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
    .studio-content-broadcast {
      padding: 1rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      background-color: var(--fts-gray-100);
    }
    .studio-content-container {
      height: 100%;
      overflow: auto;
      .fts-console {
        min-width: 1024px;
      }
      &.studio-content-template {
        .fts-module-packages {
          min-width: 640px;
        }
      }
    }
    .content-header {
      @include theme() {
        @include backdrop-background(
          $color: theme-get('fts-background'),
          $alpha: 0.72,
          $blur: 20px
        );
      }
      position: sticky;
      top: 0;
      left: 0;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      color: var(--fts-text);
      z-index: 50;
      .content-nav {
        display: flex;
        align-items: center;
        top: 0;
        left: 0;
        height: 55px;
      }
      .content-nav-l {
        flex: 1 1 0;
        min-width: 0;
        padding: 0 1rem;
        .module-wrapper {
          min-width: 0;
          display: flex;
          align-items: center;
          transform: translate(0.75rem, 100%);
          transition: 0.3s transform ease-in-out;
          visibility: hidden;
          .module-emoji {
            margin-right: 0.5rem;
          }
          .module-name {
            @include text-overflow-ellipsis;
            font-size: 1.125rem;
            font-weight: 500;
          }
          .module-tag {
            display: inline-flex;
            align-items: center;
            margin-left: 0.575rem;
            padding: 0.125rem 0.5rem;
            font-size: 0.75rem;
            line-height: 1rem;
            border-radius: 0.25rem;
            background-color: var(--drawer-tag-background, var(--drawer-secondary-background));
            color: var(--drawer-tag, var(--drawer-secondary));
          }
          &.module-wrapper--visible {
            transform: translate(0, 0);
            visibility: visible;
          }
        }
      }
      .content-nav-c {
        flex: 0 0 auto;
        .content-nav-wrapper {
          @include flex-center-middle;
          .content-nav-icon {
            margin-right: 0.575rem;
            font-size: 1.1rem;
            color: var(--fts-default);
          }
          .content-nav-title {
            @include text-overflow-hidden;
            font-size: 1.25rem;
            font-weight: 500;
          }
          .content-nav-title + .content-nav-action {
            margin-left: 0.375rem;
          }
        }
      }
      .content-nav-r {
        flex: 0 0 auto;
        padding: 0 1rem;
        .content-nav-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }
        .content-nav-checkbox {
          align-items: center;
          margin-right: 0.5rem;
          font-weight: 500;
        }
      }
    }
  }
}

.module-designer-modal {
  .helper-label {
    color: var(--fts-default);
    font-size: 0.875rem;
  }
  .emoji-select {
    display: flex;
    justify-content: center;
    margin: 1.25rem 0 1rem;
  }
  .fts-form-control {
    border-radius: 0.25rem;
  }
}
</style>
